import { createPrimerCheckoutSession, getPaymentFailUrl, getPaymentSuccessUrl } from "../payments"
import { isBasicPlanOffer } from "../helpers"
import { errorsManager } from "../services/errors/errorsManager"

const { Primer } = window

const PRIMER_LOCALES = [
  "de",
  "el",
  "en",
  "es",
  "fr",
  "pl",
  "pt",
  "bg",
  "hu",
  "ro",
  "ru",
  "sk",
  "nl",
]

// Alpine JS component that provides Primer payments provider
// common functionality withinb the checkout.
export default ({ paymentsFailPath, paymentsSuccessPath }) => ({
  showSpinner: true,
  selectedPaymentMethodType: null,

  async initCheckout() {
    const checkoutSession = await this.getCheckoutSession()

    if (!checkoutSession) {
      this.displayErrorMessage()
      return
    }

    this.showSpinner = false

    // Init and render Primer UI
    const primerUniversalCheckout = await this.renderPrimer(checkoutSession)

    // If primer doesn't support locale add from translations
    if (!this.isPrimerLocale(pipelineConfig.language)) {
      this.patchTranslations(primerUniversalCheckout)
    } else {
      this.applyCustomSubmitButton(primerUniversalCheckout, this.store.internalTracker)
    }

    // Update pipeline data
    await this.store.storeCheckoutData({ checkoutSession })
  },

  init() {
    this.store = Alpine.store("data")

    window.addEventListener("pageshow", () => {
      const primerCheckoutCardNumberId = "#primer-checkout-card-number-field"
      this.observeElementInsertion(primerCheckoutCardNumberId, this.store.internalTracker)
    })

    // Check wether the "load" event has already fired, if so draw checkout immediately
    if (document.readyState === "complete") {
      this.initCheckout()
      return
    }

    window.addEventListener("load", () => this.initCheckout())
  },

  async getCheckoutSession() {
    // If checkout session was preloaded - use it
    if (this.store.values?.isCheckoutPreloaded && this.store.values?.checkout) {
      return this.store.values?.checkout
    }

    try {
      return await createPrimerCheckoutSession({ restrictSaveCard: true })
    } catch (e) {
      console.error(e)
      this.showSpinner = false
      this.displayErrorMessage()
      return
    }
  },

  // Loads a Primer Checkout
  async renderPrimer(checkoutSession) {
    // Trigger analytics event and if fails so moves on
    try {
      this.store.internalTracker.trackSystemEvent({ eventName: "Primer Checkout Requested" })
    } catch (e) {
      console.error("Segment: Primer Checkout Requested failed", e)
    }

    try {
      const options = this.getPrimerOptions(checkoutSession)

      if (!Primer) {
        errorsManager.reportError(
          new Error("[Common] Primer SDK is not loaded"),
          { paymentSessionId: checkoutSession?.paymentSessionId }
        )
      }

      const primerUniversalCheckout = await Primer.showUniversalCheckout(
        checkoutSession.clientToken,
        options,
      )

      this.store.internalTracker.trackSystemEvent({ eventName: "Primer Checkout Loaded" })
      this.store.trackStatusForAnalytics("INITIATE_CHECKOUT")

      return primerUniversalCheckout
    } catch (e) {
      console.log(e)
      this.showSpinner = false
      this.displayErrorMessage()
      return
    }
  },

  // Displays error message with reload button that refresh the page
  displayErrorMessage() {
    const offlineBlock = window.document.getElementById("offline-msg")
    offlineBlock.style.display = "block"
  },

  async getDeclineReason(paymentSessionId, providerId) {
    const activeOffer = this.store.values.activeOffer
    const isInsufficientFundsMessage =
      pipelineConfig.frontendCustomFields?.insufficientFundsErrorMessage.value === "true"

    if (!isBasicPlanOffer(activeOffer) || !isInsufficientFundsMessage) return null

    const paymentUrl = new URL(
      `/api/v1/payments/provider-info/primer/${paymentSessionId}/${providerId}`,
      pipelineConfig.paymentsApiUrl,
    )
    const INITIAL_DELAY = 2000
    const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

    await sleep(INITIAL_DELAY)

    try {
      const response = await fetch(paymentUrl.toString(), { timeout: 60000 })

      if (!response.ok) {
        throw new Error(`Error fetching declineReason: ${response.statusText}`)
      }

      const responseData = await response.json()
      const declineReason = responseData?.declineReason

      return declineReason ?? null
    } catch (error) {
      console.error(`Fetch failed:`, error)
      throw error
    }
  },

  applyInsufficientFundsDiscount() {
    this.store.values.exitIntent = true

    return this.store.pricing.getPriceDiscount({
      offer: this.store.values.activeOffer,
      isChance: true,
    })
  },

  // Checks for the compability with locale
  isPrimerLocale(locale) {
    return PRIMER_LOCALES.includes(locale || "en")
  },

  // Primer Checkout options - config, styles, buttons, texts etc.
  getPrimerOptions(checkoutSession) {
    const markPaymentAsFailed = this.markPaymentAsFailed.bind(this)

    const options = {
      container: "#checkout-container",
      locale: pipelineConfig.language || "en",
      vault: {
        visible: false,
      },
      successScreen: false,
      errorMessage: false,
      onPaymentCreationStart: () => {
        this.store.trackStatusForAnalytics("SUBMIT_CC_INPUT")
      },
      onCheckoutComplete: async () => {
        window.location = getPaymentSuccessUrl({ checkoutSession, pagePath: paymentsSuccessPath })
      },
      onCheckoutFail: async (error, { payment }) => {
        this.store.values.isPaymentLoading = false

        await markPaymentAsFailed()

        try {
          const declineReason = await this.getDeclineReason(
            checkoutSession.paymentSessionId,
            payment.id,
          )

          if (declineReason === "INSUFFICIENT_FUNDS") {
            this.applyInsufficientFundsDiscount()
            window.location = getPaymentFailUrl({
              paymode: "insufficient",
              pagePath: paymentsFailPath,
            })

            return
          }
        } catch (error) {
          console.warn("Could not determine insufficient funds reason:", error)
        }
        window.location = getPaymentFailUrl({ paymode: "primer", pagePath: paymentsFailPath })
      },

      onPaymentMethodAction: (action, data) => {
        if (
          action === "PAYMENT_METHOD_SELECTED" &&
          data.paymentMethodType !== this.selectedPaymentMethodType
        ) {
          this.selectedPaymentMethodType = data.paymentMethodType
        }
      },

      onBeforePaymentCreate: ({ paymentMethodType }, handler) => {
        const trackingEventNames = {
          payment_card: "Credit Card Submitted",
          google_pay: "Google Pay Selected",
          apple_pay: "Apple Pay Selected",
          paypal: "PayPal Selected",
        }

        try {
          const clickName = trackingEventNames[paymentMethodType.toLowerCase()] || paymentMethodType
          this.store.internalTracker.trackClick({ clickName })
        } catch (error) {
          console.error("Error in onBeforePaymentCreate:", error)
        } finally {
          handler.continuePaymentCreation()
        }
      },

      submitButton: {
        useBuiltInButton: this.isPrimerLocale(pipelineConfig.language),
        onVisible: (isVisible) => {
          const submitButton = window.document.getElementById("submit-button")
          if (!submitButton) return

          if (isVisible) {
            submitButton.style.display = "flex"
          } else {
            submitButton.style.display = "none"
          }
        },
        onDisable: (isDisabled) => {
          const submitButton = window.document.getElementById("submit-button")
          if (!submitButton) return

          submitButton.disabled = isDisabled
          this.store.values.isPaymentLoading = isDisabled
        },
        onLoading: (isLoading) => {
          const submitButton = window.document.getElementById("submit-button")
          if (!submitButton) return

          if (isLoading) {
            submitButton.getElementsByClassName("button-loader")[0].style.display = "block"
          } else {
            submitButton.getElementsByClassName("button-loader")[0].style.display = "none"
          }
        },
      },
      card: {
        cardholderName: {
          placeholder: "",
        },
      },
      paypal: {
        buttonColor: "gold",
        buttonShape: "rect",
        paymentFlow: "PREFER_VAULT",
      },
      googlePay: {
        captureBillingAddress: true,
      },
      applePay: {
        captureBillingAddress: true,
      },
      style: {
        formSpacings: {
          betweenLabelAndInput: "12px",
          betweenInputs: "16px",
        },
        inputLabel: {
          fontFamily: "Avenir, Arial, sans-serif",
          fontSize: "12px",
          lineHeight: "normal",
          fontWeight: "normal",
          color: "#212121",
        },
        input: {
          base: {
            background: "#fff",
            color: "#35383C",
          },
        },
        inputErrorText: {
          fontSize: "14px",
          fontWeight: "400",
        },
        submitButton: {
          base: {
            fontFamily: "JosefinSans, sans-serif",
            color: "#fff",
            background: "#46A477",
            fontSize: "20px",
            fontWeight: "bold",
            borderRadius: "6px",
          },
        },
      },
    }

    return options
  },

  // Patch the primer translations with ours
  patchTranslations(primerUniversalCheckout) {
    const applyCustomLabel = (field) => {
      // Remove default primer label
      const currentField = window.document.getElementById(field.id)
      currentField.getElementsByClassName("PrimerCheckout__label")[0].style.display = "none"

      // Create ours
      const newField = window.document.createElement("div")
      newField.dataset.testid = "CreditCardForm.inputLabel"
      newField.classList.add("primer-input-label")
      newField.classList.add("PrimerCheckout__label")
      newField.innerHTML = field.translation

      // Display our label
      currentField.insertBefore(newField, currentField.firstChild)
    }

    const applyCustomErrorMessage = (field) => {
      const currentField = document.getElementById(field.id)
      const targetNode = currentField.getElementsByTagName("div")[0]

      // Mutation config
      const config = { childList: true }

      // Callback function executes on dom changes
      const callback = (mutationList) => {
        for (const mutation of mutationList) {
          if (mutation.type === "childList") {
            // Apply our custom translation
            if (targetNode.innerHTML !== field.translation && targetNode.innerHTML !== "") {
              targetNode.innerHTML = field.translation
            }
          }
        }
      }
      // Create/Observe the node mutations
      const observer = new MutationObserver(callback)
      observer.observe(targetNode, config)
    }

    const primerFields = [
      {
        labelField: {
          id: "primer-checkout-card-number-field",
          translation: primerTranslations.cardnumber_label,
        },
        errorField: {
          id: "primer-checkout-card-number",
          translation: primerTranslations.error.empty_field,
        },
      },
      {
        labelField: {
          id: "primer-checkout-card-expirydate-field",
          translation: primerTranslations.expire_label,
        },
        errorField: {
          id: "primer-checkout-card-expirydate-error",
          translation: primerTranslations.error.empty_field,
        },
      },
      {
        labelField: {
          id: "primer-checkout-card-cvv-field",
          translation: primerTranslations.cvc_label,
        },
        errorField: {
          id: "primer-checkout-card-cvv-error",
          translation: primerTranslations.error.empty_field,
        },
      },
      {
        labelField: {
          id: "primer-checkout-cardholder-name-field",
          translation: primerTranslations.username_label,
        },
        errorField: {
          id: "primer-checkout-cardholder-name-error",
          translation: primerTranslations.error.empty_field,
        },
      },
    ]

    primerFields.forEach((field) => {
      applyCustomLabel(field.labelField)
      applyCustomErrorMessage(field.errorField)
    })

    this.applyCustomSubmitButton(primerUniversalCheckout)
  },

  applyCustomSubmitButton(primerUniversalCheckout) {
    // Hide primer original button
    const primerSubmitButton = window.document.getElementById(
      "primer-checkout-submit-button-container",
    )
    primerSubmitButton.style.display = "none"

    const primerWindow = window.document.getElementById("primer-checkout")
    const buttonElement = window.document.createElement("button")
    const loaderElement = window.document.createElement("div")
    const spanElement = window.document.createElement("span")

    // Apply button params
    buttonElement.id = "submit-button"
    buttonElement.className = "submit-button"
    buttonElement.name = "submit"
    buttonElement.type = "button"
    buttonElement.addEventListener(
      "click",
      () => {
        primerUniversalCheckout.submit()
      },
      false,
    )
    // Apply loader params
    loaderElement.className = "button-loader"
    // Apply span params
    spanElement.className = "submit-button-text"
    spanElement.innerHTML = primerTranslations.button

    buttonElement.appendChild(spanElement)
    buttonElement.appendChild(loaderElement)
    primerWindow.appendChild(buttonElement)
  },

  async markPaymentAsFailed() {
    this.store.values.failedPayment = true

    this.store.trackStatusForAnalytics("FAILED_CHECKOUT")
  },

  observeElementInsertion(selector, internalTracker) {
    if (!window.MutationObserver) {
      console.error("MutationObserver is not supported in this browser.")
      return
    }

    // Callback function for when a mutation occurs
    const observerCallback = (mutationsList, observer) => {
      for (const mutation of mutationsList) {
        if (mutation.type === "childList") {
          // Check if the element now exists in the DOM
          const element = document.querySelector(selector)
          if (element) {
            // Once found log event to segment and disconnect observer
            internalTracker.trackSystemEvent({
              eventName: "Primer Checkout Loaded Confirmation",
            })
            observer.disconnect()
            break
          }
        }
      }
    }

    // Create a MutationObserver with the callback function
    const observer = new MutationObserver(observerCallback)

    // Start observing the document's body for added nodes
    observer.observe(document.body, { childList: true, subtree: true })
  },
})
